import { FeedbackLink } from "@components/FeedbackLink";
import { getGranteeName } from "@components/donate/DonateV3/FAQ";
import styled from "@emotion/styled";
import React from "react";

import { FundraiserResponse } from "@every.org/common/src/codecs/entities";
import { TextSize } from "@every.org/common/src/display/text";
import {
  EVERYDOTORG_NONPROFIT_IDS,
  SPECIAL_FUNDRAISER_NONPROFIT_ID,
} from "@every.org/common/src/entity/constants";
import { DonationFlowPaymentOption } from "@every.org/common/src/entity/types";
import {
  ClientRouteName,
  getRoutePath,
  URLFormat,
} from "@every.org/common/src/helpers/clientRoutes";
import { isOfficialFundraiser } from "@every.org/common/src/helpers/fundraisers";

import { Link } from "src/components/Link";
import { useParentNonprofitWithoutDisbursement } from "src/context/NonprofitsContext/hooks";
import { ContextNonprofit } from "src/context/NonprofitsContext/types";
import { colorCssVars } from "src/theme/color";
import { cssForMediaSize, MediaSize } from "src/theme/mediaQueries";
import { spacing, verticalStackCss } from "src/theme/spacing";
import { textSizeCss } from "src/theme/text";

const StyledFooter = styled.footer`
  ${verticalStackCss.xs}
  padding: ${spacing.l};
  ${textSizeCss[TextSize.xs]}
  color: var(${colorCssVars.text.secondary});
  a {
    color: var(${colorCssVars.text.secondary});
    text-decoration: underline;
  }
  a:hover {
    color: var(${colorCssVars.text.secondary});
    text-decoration: underline;
  }
  ${cssForMediaSize({
    min: MediaSize.LARGE,
    css: { padding: `0 ${spacing.m}` },
  })}
`;

export const DAF_DEDUCTIBLE_STATEMENT =
  "If you use a Donor Advised Fund (DAF), your receipt from Every.org will not be tax-deductible as the tax deduction was already received at the time you contributed to your DAF. For any other payment method, you will get a tax-deductible receipt emailed to you";

export const IRA_DEDUCTIBLE_STATEMENT =
  "If you give through a Qualified Charitable Distribution (QCD) from your IRA, your receipt from Every.org will not be tax-deductible as QCD gifts are excluded from your taxable income rather than deducted. For any other payment method, you will get a tax-deductible receipt emailed to you";

export const getTaxDeductibleStatement = (
  nonprofit: ContextNonprofit,
  fundraiser?: FundraiserResponse,
  parentNonprofit?: ContextNonprofit,
  paymentOption?: DonationFlowPaymentOption
) => {
  const granteeName = getGranteeName(nonprofit, parentNonprofit);
  const isEverydotorgRecipient = EVERYDOTORG_NONPROFIT_IDS.includes(
    nonprofit.id
  );
  const deductibleStatement =
    paymentOption === DonationFlowPaymentOption.DAF
      ? DAF_DEDUCTIBLE_STATEMENT
      : paymentOption === DonationFlowPaymentOption.IRA
      ? IRA_DEDUCTIBLE_STATEMENT
      : "100% of your donation is tax-deductible to the extent allowed by US law";

  const main = `${deductibleStatement}. Your donation is made to Every.org, a tax-exempt US 501(c)(3) Public Charity${
    isEverydotorgRecipient
      ? "."
      : ` that
      grants unrestricted funds to ${granteeName} on your behalf. 
      As a legal matter, Every.org must provide any donations to ${granteeName} on an
      unrestricted basis, regardless of any designations or restrictions made by
      you.`
  }`;

  if (isEverydotorgRecipient) {
    const customTaxDeductible = nonprofit.metadata?.customTaxDeductible || "";
    return (
      <span>
        {main} {customTaxDeductible}
      </span>
    );
  }

  const isUnofficialFundraiser =
    fundraiser && !isOfficialFundraiser(fundraiser);

  let noPermission = "";

  const nonprofitNameWithVerb =
    nonprofit.id === SPECIAL_FUNDRAISER_NONPROFIT_ID
      ? "the nonprofits have"
      : `${nonprofit.name} has`;
  if (nonprofit.hasAdmin && isUnofficialFundraiser) {
    noPermission = `Please note ${nonprofitNameWithVerb} not reviewed or approved the content of this peer-to-peer fundraiser.`;
  } else if (!nonprofit.hasAdmin && !isUnofficialFundraiser) {
    noPermission = `Please note ${nonprofitNameWithVerb} not provided permission for this page.`;
  } else if (!nonprofit.hasAdmin && isUnofficialFundraiser) {
    noPermission = `Please note ${nonprofitNameWithVerb} not provided permission for this page or reviewed or approved the content of this peer-to-peer fundraiser.`;
  }

  const customTaxDeductible = nonprofit.metadata?.customTaxDeductible || "";

  return (
    <span>
      {main} {noPermission} {customTaxDeductible}
    </span>
  );
};

export const Footer = ({
  nonprofit,
  fundraiser,
  paymentOption,
}: {
  nonprofit: ContextNonprofit;
  fundraiser?: FundraiserResponse;
  paymentOption?: DonationFlowPaymentOption;
}) => {
  const parentNonprofit = useParentNonprofitWithoutDisbursement(nonprofit);

  return (
    <StyledFooter>
      <p>
        {getTaxDeductibleStatement(
          nonprofit,
          fundraiser,
          parentNonprofit,
          paymentOption
        )}{" "}
        <Link
          title="Link to Terms of Service"
          to={getRoutePath({
            format: URLFormat.RELATIVE,
            name: ClientRouteName.TERMS_OF_SERVICE,
          })}
          data-tname="Footer--Terms"
          target="terms"
        >
          See Terms
        </Link>
        .
      </p>
      <p>
        For important information regarding charitable solicitations, please
        review our{" "}
        <Link
          title="Link to Privacy Policy"
          to="https://support.every.org/hc/en-us/articles/5968895910291-What-are-your-Nonprofit-State-Disclosures"
          data-tname="Footer--Disclosures"
          target="disclosures"
          forceExternal
        >
          state-specific disclosures
        </Link>
        .
      </p>
      <p>
        Need help? See FAQs or contact us at our{" "}
        <Link
          title="Link to Support"
          to={getRoutePath({
            format: URLFormat.RELATIVE,
            name: ClientRouteName.SUPPORT,
          })}
          data-tname="Footer--Support"
          target="support"
        >
          Help Center
        </Link>
        .
      </p>
      <p>
        Have ideas for how we can build a better donation experience? Send us{" "}
        <FeedbackLink
          title="Open feedback widget"
          data-tname="Footer--Feedback"
        >
          feedback
        </FeedbackLink>
        .
      </p>
      <p>
        We respect your privacy. To learn more, check out our{" "}
        <Link
          title="Link to Privacy Policy"
          to={getRoutePath({
            format: URLFormat.RELATIVE,
            name: ClientRouteName.PRIVACY_POLICY,
          })}
          data-tname="Footer--Privacy"
          target="privacy"
        >
          Privacy Policy
        </Link>
        .
      </p>
    </StyledFooter>
  );
};
